import { Box, Center, Container, Group, Stack, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { useListOrganisations } from '@/api/generated/organisations/organisations';
import { useSearchUsers } from '@/api/generated/users/users';
import {
  CardSection,
  ListPagination,
  MobileListFilters,
  PageHeading,
  SearchField,
  SelectField,
  StatusPill,
  TABLE_STATUS_COL_WIDTH,
  tableCenteredCellStyle,
  tableRowStyle
} from '@/components/common';
import { formatDateTime, formatRole } from '@/utils/format';
import { usePageSize } from '@/utils/usePageSize';

const ROLE_VALUES = ['USER', 'ORGANISATION_ADMIN', 'APP_ADMIN'] as const;

const userColumns: [string, number, ('center' | undefined)?][] = [
  ['adminUsers.colUser', 220],
  ['platformAdmin.colOrganization', 220],
  ['adminOrgDetail.colRole', 140],
  ['platformAdmin.colStatus', TABLE_STATUS_COL_WIDTH, 'center']
];

export const AdminUsersPage = () => {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [orgFilter, setOrgFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = usePageSize();

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, orgFilter, roleFilter, statusFilter, pageSize]);

  // staleTime: 0 — druga sesija (npr. deaktivacija) može promijeniti stanje
  // bez signala natrag ovom ekranu, isti razlog kao ostale admin liste.
  const { data, isLoading } = useSearchUsers(
    {
      page,
      pageSize,
      search: debouncedSearch.trim() || undefined,
      organisationId: orgFilter === 'ALL' ? undefined : orgFilter,
      role: roleFilter === 'ALL' ? undefined : roleFilter,
      isActive: statusFilter === 'ALL' ? undefined : statusFilter === 'ACTIVE'
    },
    { query: { staleTime: 0 } }
  );
  const pageItems = data?.content ?? [];
  const totalElements = data?.totalElements ?? 0;
  const totalPages = data?.totalPages ?? 1;

  const { data: orgsData } = useListOrganisations();
  const orgOptions = [
    { value: 'ALL', label: t('adminUsers.filterOrgPlaceholder') },
    ...(orgsData ?? []).map((org) => ({ value: org.id ?? '', label: org.name ?? '' }))
  ];
  const roleOptions = [
    { value: 'ALL', label: t('adminUsers.filterRolePlaceholder') },
    ...ROLE_VALUES.map((role) => ({ value: role, label: formatRole(role) }))
  ];
  const statusOptions = [
    { value: 'ALL', label: t('adminOrganizations.filterStatusAll') },
    { value: 'ACTIVE', label: t('platformAdmin.statusActive') },
    { value: 'INACTIVE', label: t('adminUsers.statusDeactivated') }
  ];

  const paginationLabel = t('users.roles.showing', {
    from: totalElements === 0 ? 0 : (page - 1) * pageSize + 1,
    to: Math.min(page * pageSize, totalElements),
    total: totalElements
  });

  return (
    <Container size="lg" px={0}>
      <Stack gap={24}>
        <PageHeading title={t('adminUsers.title')} subtitle={t('adminUsers.subtitle')} />

        <Group gap={16} align="flex-end" wrap="wrap" visibleFrom="sm">
          <Box w={{ base: '100%', sm: 280 }}>
            <SearchField
              hideLabel
              placeholder={t('adminUsers.searchPlaceholder')}
              value={search}
              onChange={(event) => {
                setSearch(event.currentTarget.value);
              }}
            />
          </Box>
          <div style={{ width: 200 }}>
            <SelectField
              label={t('adminUsers.filterOrg')}
              data={orgOptions}
              value={orgFilter}
              onChange={(value) => {
                setOrgFilter(value ?? 'ALL');
              }}
              allowDeselect={false}
            />
          </div>
          <div style={{ width: 160 }}>
            <SelectField
              label={t('adminUsers.filterRole')}
              data={roleOptions}
              value={roleFilter}
              onChange={(value) => {
                setRoleFilter(value ?? 'ALL');
              }}
              allowDeselect={false}
            />
          </div>
          <div style={{ width: 160 }}>
            <SelectField
              label={t('adminOrganizations.filterStatus')}
              data={statusOptions}
              value={statusFilter}
              onChange={(value) => {
                setStatusFilter(value ?? 'ALL');
              }}
              allowDeselect={false}
            />
          </div>
        </Group>

        <Box hiddenFrom="sm">
          <MobileListFilters
            search={
              <SearchField
                hideLabel
                placeholder={t('adminUsers.searchPlaceholder')}
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
            }
            filters={[
              {
                key: 'org',
                label: t('adminUsers.filterOrg'),
                options: orgOptions,
                value: orgFilter,
                onChange: (value) => setOrgFilter(value ?? 'ALL'),
                emptyValue: 'ALL'
              },
              {
                key: 'role',
                label: t('adminUsers.filterRole'),
                options: roleOptions,
                value: roleFilter,
                onChange: (value) => setRoleFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              },
              {
                key: 'status',
                label: t('adminOrganizations.filterStatus'),
                options: statusOptions,
                value: statusFilter,
                onChange: (value) => setStatusFilter(value ?? 'ALL'),
                emptyValue: 'ALL',
                variant: 'buttons'
              }
            ]}
          />
        </Box>

        <CardSection>
          {!isLoading && pageItems.length === 0 && (
            <Center py={40}>
              <Text fz={13} style={{ color: 'var(--color-text-muted)' }}>
                {t('users.roles.emptyState')}
              </Text>
            </Center>
          )}

          {pageItems.length > 0 && (
            <>
              <Box style={{ overflowX: 'auto' }} visibleFrom="sm">
                <div style={{ minWidth: 1000 }}>
                  <div
                    className="table-header-row"
                    style={{
                      ...tableRowStyle,
                      padding: '12px 20px',
                      backgroundColor: 'var(--color-bg-canvas)',
                      borderBottom: '1px solid var(--color-border-soft)'
                    }}
                  >
                    {userColumns.map(([key, width, align]) => (
                      <Text
                        key={key}
                        fz={10}
                        fw={600}
                        w={width}
                        ta={align}
                        style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                      >
                        {t(key)}
                      </Text>
                    ))}
                    <Text
                      fz={10}
                      fw={600}
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4, flex: 1 }}
                    >
                      {t('adminOrgDetail.colLastLogin')}
                    </Text>
                    <Text
                      fz={10}
                      fw={600}
                      w={100}
                      ta="right"
                      style={{ color: 'var(--color-text-muted)', letterSpacing: 0.4 }}
                    >
                      {t('platformAdmin.colAction')}
                    </Text>
                  </div>

                  {pageItems.map((user, index) => (
                    <div
                      key={user.id}
                      style={{
                        ...tableRowStyle,
                        padding: '14px 20px',
                        borderTop: index === 0 ? undefined : '1px solid var(--color-border-soft)'
                      }}
                    >
                      <Stack gap={2} style={{ width: 220 }}>
                        <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                          {user.firstName} {user.lastName}
                        </Text>
                        <Text fz={11} style={{ color: 'var(--color-text-muted)' }}>
                          {user.email}
                        </Text>
                      </Stack>
                      <Text
                        fz={13}
                        fw={500}
                        w={220}
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        {user.orgName}
                      </Text>
                      <Text fz={13} w={140} style={{ color: 'var(--color-text-secondary)' }}>
                        {formatRole(user.role)}
                      </Text>
                      <div style={{ ...tableCenteredCellStyle, width: TABLE_STATUS_COL_WIDTH }}>
                        <StatusPill
                          label={t(
                            user.isActive
                              ? 'platformAdmin.statusActive'
                              : 'adminUsers.statusDeactivated'
                          )}
                          bg={
                            user.isActive
                              ? 'var(--color-status-success-soft)'
                              : 'var(--color-status-danger-soft)'
                          }
                          fg={
                            user.isActive
                              ? 'var(--color-status-success)'
                              : 'var(--color-status-danger)'
                          }
                        />
                      </div>
                      <Text
                        fz={13}
                        style={{
                          color: 'var(--color-text-muted)',
                          flex: 1,
                          fontFamily: 'var(--mantine-font-family-monospace)'
                        }}
                      >
                        {formatDateTime(user.lastLoginAt) ?? t('users.roles.neverLoggedIn')}
                      </Text>
                      <Text
                        component={Link}
                        to={`/admin/users/${user.id}`}
                        fz={12}
                        fw={600}
                        w={100}
                        ta="right"
                        style={{
                          color: 'var(--color-accent-primary-active)',
                          textDecoration: 'underline'
                        }}
                      >
                        {t('platformAdmin.detailsLink')}
                      </Text>
                    </div>
                  ))}
                </div>
              </Box>

              <ListPagination
                label={paginationLabel}
                page={page}
                totalPages={totalPages}
                onPageChange={setPage}
                pageSize={pageSize}
                onPageSizeChange={setPageSize}
              />
            </>
          )}
        </CardSection>

        <Stack gap={12} hiddenFrom="sm">
          {pageItems.map((user) => (
            <Stack
              key={user.id}
              gap={12}
              p={16}
              style={{
                width: '100%',
                borderRadius: 12,
                border: '1px solid var(--color-border-default)',
                backgroundColor: 'var(--color-bg-surface)'
              }}
            >
              <Group justify="space-between" align="flex-start" wrap="nowrap">
                <Text fz={13.5} fw={600} style={{ color: 'var(--color-text-primary)' }}>
                  {user.firstName} {user.lastName}
                </Text>
                <StatusPill
                  label={t(
                    user.isActive ? 'platformAdmin.statusActive' : 'adminUsers.statusDeactivated'
                  )}
                  bg={
                    user.isActive
                      ? 'var(--color-status-success-soft)'
                      : 'var(--color-status-danger-soft)'
                  }
                  fg={user.isActive ? 'var(--color-status-success)' : 'var(--color-status-danger)'}
                />
              </Group>
              <Text fz={11.5} style={{ color: 'var(--color-text-secondary)' }}>
                {user.email}
              </Text>
              <Group gap={12} wrap="wrap">
                <Group gap={4} wrap="nowrap">
                  <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                    {t('adminUsers.colOrgShort')}:
                  </Text>
                  <Text
                    fz={11}
                    truncate
                    style={{ color: 'var(--color-text-secondary)', maxWidth: 120 }}
                  >
                    {user.orgName}
                  </Text>
                </Group>
                <Group gap={4} wrap="nowrap">
                  <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                    {t('adminOrgDetail.colRole')}:
                  </Text>
                  <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                    {formatRole(user.role)}
                  </Text>
                </Group>
              </Group>
              <Group gap={4} wrap="nowrap">
                <Text fz={11} fw={600} style={{ color: 'var(--color-text-muted)' }}>
                  {t('adminOrgDetail.colLastLogin')}:
                </Text>
                <Text fz={11} style={{ color: 'var(--color-text-secondary)' }}>
                  {formatDateTime(user.lastLoginAt) ?? t('users.roles.neverLoggedIn')}
                </Text>
              </Group>
              <Box pt={10} style={{ borderTop: '1px solid var(--color-border-soft)' }}>
                <Text
                  component={Link}
                  to={`/admin/users/${user.id}`}
                  fz={12}
                  fw={600}
                  ta="right"
                  style={{
                    color: 'var(--color-accent-primary-active)',
                    textDecoration: 'underline'
                  }}
                >
                  {t('platformAdmin.detailsLink')}
                </Text>
              </Box>
            </Stack>
          ))}

          {pageItems.length > 0 && (
            <ListPagination
              label={paginationLabel}
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              bordered={false}
            />
          )}
        </Stack>
      </Stack>
    </Container>
  );
};
